import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, EmptyState, Modal } from '../../components';
import { getCourses, deleteCourse, togglePublishCourse, archiveCourse, unarchiveCourse, CourseDoc } from '../../services/course-service';
import { getCourseEnrollments, EnrollmentWithUser } from '../../services/enrollment-service';

const MenuItem: React.FC<{ onClick: () => void; danger?: boolean; children: React.ReactNode }> = ({ onClick, danger, children }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-line/50 ${danger ? 'text-danger hover:text-danger/70' : 'text-text'}`}
  >
    {children}
  </button>
);

const statusColors: Record<string, string> = {
  published: 'bg-success/20 text-success',
  draft:     'bg-warning/20 text-warning',
  archived:  'bg-muted/20 text-muted',
};

const levelColors: Record<string, string> = {
  beginner:     'bg-info/20 text-info',
  intermediate: 'bg-warning/20 text-warning',
  advanced:     'bg-danger/20 text-danger',
};

type StatusFilter = 'all' | 'published' | 'draft' | 'archived';

const AdminCourses: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [learnersModal, setLearnersModal] = useState<{ courseId: string; courseTitle: string } | null>(null);
  const [learners, setLearners] = useState<EnrollmentWithUser[]>([]);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getCourses()
      .then(setCourses)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDelete = (id: string, title: string) => {
    setDeleteError('');
    setDeleteTarget({ id, title });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await deleteCourse(deleteTarget.id);
      setCourses(prev => prev.filter(c => c._id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e: any) {
      setDeleteError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      const course = courses.find(c => c._id === id);
      if (!course) return;
      const updated = await togglePublishCourse(id, course.status);
      setCourses(prev => prev.map(c => c._id === id ? updated : c));
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      const updated = await archiveCourse(id);
      setCourses(prev => prev.map(c => c._id === id ? updated : c));
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      const updated = await unarchiveCourse(id);
      setCourses(prev => prev.map(c => c._id === id ? updated : c));
    } catch (e: any) {
      alert(e.message);
    }
  };

  const filtered = courses
    .filter(c => {
      const statusMatch = statusFilter === 'all' || c.status === statusFilter;
      const searchMatch = c.title.toLowerCase().includes(search.toLowerCase());
      return statusMatch && searchMatch;
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  const stats = {
    total:     courses.length,
    published: courses.filter(c => c.status === 'published').length,
    draft:     courses.filter(c => c.status === 'draft').length,
    archived:  courses.filter(c => c.status === 'archived').length,
  };

  const totalItems = (course: CourseDoc) =>
    course.modules.reduce((sum, m) => sum + m.items.length, 0);

  const openLearnersModal = async (courseId: string, courseTitle: string) => {
    setLearnersModal({ courseId, courseTitle });
    setLearnersLoading(true);
    try {
      const data = await getCourseEnrollments(courseId);
      setLearners(data);
    } catch (e: any) {
      setLearners([]);
    } finally {
      setLearnersLoading(false);
    }
  };

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text">Courses</h1>
          <p className="text-muted mt-1">Manage your learning content</p>
        </div>
        <Button variant="primary" onClick={() => navigate('/app/admin/courses/new')}>
          + Add Course
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Courses', value: stats.total },
          { label: 'Published',     value: stats.published },
          { label: 'Drafts',        value: stats.draft },
          { label: 'Archived',      value: stats.archived },
        ].map(s => (
          <Card key={s.label}>
            <div className="text-center">
              <div className="text-2xl font-bold text-chart mb-1">{s.value}</div>
              <div className="text-muted text-sm">{s.label}</div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex gap-2">
          {(['all', 'published', 'draft', 'archived'] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                statusFilter === f
                  ? 'bg-chart text-bg'
                  : 'bg-surface border border-line text-muted hover:text-text'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto w-full sm:w-64">
          <Input
            placeholder="Search by title..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">
          {error}
        </div>
      )}

      {/* Course List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No courses found"
          description={statusFilter !== 'all' || search ? 'Try adjusting your filters.' : 'Create your first course to get started.'}
          action={statusFilter === 'all' && !search ? (
            <Button variant="primary" onClick={() => navigate('/app/admin/courses/new')}>
              Add Course
            </Button>
          ) : undefined}
        />
      ) : (
        <div className="space-y-4" ref={menuRef}>
          {filtered.map(course => (
            <Card key={course._id} className="overflow-visible">
              <div className="flex items-center gap-4">
                {/* Thumbnail */}
                <div className="w-20 h-14 rounded-lg bg-muted/10 border border-line flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {course.thumbnailUrl ? (
                    <img src={course.thumbnailUrl} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-8 h-8 text-muted" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
                    </svg>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-text font-semibold truncate">{course.title}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-surface border border-line text-muted">
                      {course.category}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${levelColors[course.level] || 'bg-muted/20 text-muted'}`}>
                      {course.level}
                    </span>
                  </div>
                  <div className="text-muted text-sm">
                    {course.modules.length} module{course.modules.length !== 1 ? 's' : ''} · {totalItems(course)} item{totalItems(course) !== 1 ? 's' : ''} · Created {new Date(course.createdAt).toLocaleDateString()}
                  </div>
                </div>

                {/* Status */}
                <span className={`text-xs px-2 py-1 rounded capitalize font-medium flex-shrink-0 ${statusColors[course.status]}`}>
                  {course.status}
                </span>

                {/* Actions */}
                <div className="relative flex-shrink-0" onMouseDown={e => e.stopPropagation()}>
                  <button
                    onClick={() => setOpenMenuId(openMenuId === course._id ? null : course._id)}
                    className="p-2 rounded-lg text-muted hover:text-text hover:bg-surface transition-colors"
                    aria-label="Course actions"
                  >
                    ⋯
                  </button>
                  {openMenuId === course._id && (
                    <div className="absolute right-0 top-full mt-1 w-44 max-w-none bg-surface border border-line rounded-lg shadow-lg z-20 py-1">
                      <MenuItem onClick={() => { openLearnersModal(course._id, course.title); setOpenMenuId(null); }}>Learners</MenuItem>
                      <MenuItem onClick={() => { navigate(`/app/admin/courses/${course._id}/edit`); setOpenMenuId(null); }}>Edit</MenuItem>
                      <div className="border-t border-line my-1" />
                      {course.status !== 'published' && (
                        <MenuItem onClick={() => { handlePublish(course._id); setOpenMenuId(null); }}>Publish</MenuItem>
                      )}
                      {course.status === 'published' && (
                        <MenuItem onClick={() => { handlePublish(course._id); setOpenMenuId(null); }}>Unpublish</MenuItem>
                      )}
                      {course.status !== 'archived' && (
                        <MenuItem onClick={() => { handleArchive(course._id); setOpenMenuId(null); }}>Archive</MenuItem>
                      )}
                      {course.status === 'archived' && (
                        <MenuItem onClick={() => { handleUnarchive(course._id); setOpenMenuId(null); }}>Unarchive</MenuItem>
                      )}
                      <div className="border-t border-line my-1" />
                      <MenuItem danger onClick={() => { handleDelete(course._id, course.title); setOpenMenuId(null); }}>Delete</MenuItem>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {/* Learners Modal */}
      <Modal
        isOpen={Boolean(learnersModal)}
        title={learnersModal ? `${learnersModal.courseTitle} — Learners` : ''}
        onClose={() => setLearnersModal(null)}
      >
        {learnersLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-chart border-t-transparent rounded-full animate-spin" />
          </div>
        ) : learners.length === 0 ? (
          <p className="text-muted text-sm text-center py-8">No learners enrolled yet.</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {learners.map(e => {
              const course = e.courseId;
              const total = course?.modules?.reduce((s: number, m: any) => s + m.items.length, 0) || 0;
              const pct = total > 0 ? Math.round((e.completedItems.length / total) * 100) : 0;
              return (
                <div key={e._id} className="flex items-center gap-3 py-2 border-b border-line last:border-0">
                  {e.userId.avatar ? (
                    <img src={e.userId.avatar} alt={e.userId.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-chart/20 flex items-center justify-center text-chart text-sm font-bold flex-shrink-0">
                      {e.userId.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-text text-sm font-medium truncate">{e.userId.name}</div>
                    <div className="text-muted text-xs truncate">{e.userId.email}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                        <div className="h-full bg-chart rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-muted text-xs flex-shrink-0">{pct}%</span>
                    </div>
                  </div>
                  <div className="text-muted text-xs flex-shrink-0">
                    {new Date(e.enrolledAt).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(deleteTarget)}
        title="Delete Course"
        onClose={() => { if (!deleteLoading) setDeleteTarget(null); }}
      >
        <div className="space-y-4">
          <p className="text-text">
            Are you sure you want to delete <strong>'{deleteTarget?.title}'</strong>?
          </p>
          <p className="text-muted text-sm">
            This will permanently remove all modules and uploaded files.
          </p>
          {deleteError && (
            <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">
              {deleteError}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              disabled={deleteLoading}
              className="!bg-danger hover:!bg-danger/90 !border-danger"
            >
              {deleteLoading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminCourses;
